import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useTutorialVideos } from '../../contexts/TutorialVideoContext';
import { logout } from '../../services/api';

function ProfileDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const { user, clearUser, deletionPending, deletionPermanentAt, showUsername } = useUser();
  const { clearVideos } = useTutorialVideos();
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    clearUser();
    clearVideos();
    setIsOpen(false);
    navigate('/login');
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button/Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-text hover:text-primary transition"
      >
        {/* User Icon with optional deletion badge */}
        <span className="relative inline-block">
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {deletionPending && (
            <span
              style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                width: '14px',
                height: '14px',
                backgroundColor: '#ef4444',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '9px',
                fontWeight: 'bold',
                color: '#fff',
                lineHeight: 1,
              }}
            >
              !
            </span>
          )}
        </span>
        <span className="hidden lg:inline">{showUsername ? user.username : 'Account'}</span>
        {/* Chevron */}
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-surface border border-muted rounded-lg shadow-xl z-50">
          <div className="py-1">
            {/* Username display in dropdown */}
            <div className="px-4 py-2 border-b border-muted">
              <p className="text-sm text-muted">Signed in as</p>
              <p className="text-brand-white font-medium truncate">{showUsername ? user.username : 'Account'}</p>
            </div>

            {/* Deletion warning banner */}
            {deletionPending && (
              <div style={{
                padding: '8px 12px',
                margin: '4px 8px',
                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '6px',
                fontSize: '12px',
                lineHeight: '1.4',
                color: '#fca5a5',
              }}>
                <strong style={{ color: '#ef4444' }}>Account deletion pending</strong>
                {deletionPermanentAt && (
                  <span> — {Math.max(0, Math.ceil((new Date(deletionPermanentAt) - new Date()) / (1000 * 60 * 60 * 24)))} days remaining</span>
                )}
                <div style={{ marginTop: '4px', color: '#d1d5db' }}>
                  Go to <Link
                    to="/profile"
                    onClick={() => setIsOpen(false)}
                    style={{ color: '#218aff', textDecoration: 'underline' }}
                  >Account Settings</Link> to cancel.
                </div>
              </div>
            )}

            {/* Profile link */}
            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-2 text-text hover:bg-gray-800 hover:text-primary transition"
            >
              Account Settings
            </Link>

            {/* Usage link */}
            <Link
              to="/usage"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-2 text-text hover:bg-gray-800 hover:text-primary transition"
            >
              Usage
            </Link>

            {/* Subscription link */}
            <Link
              to="/subscription"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-2 text-text hover:bg-gray-800 hover:text-primary transition"
            >
              Subscription
            </Link>

            {/* Affiliate Program link */}
            <Link
              to="/affiliate"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-2 text-text hover:bg-gray-800 hover:text-primary transition"
            >
              Affiliate Program
            </Link>

            {/* Logout button */}
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-text hover:bg-red-900/20 hover:text-red-400 transition border-t border-muted"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileDropdown;
